import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type {
  AutoPinpointEmailCampaign,
  BaseCampaign,
  DripCampaign,
  EmailListFacebookCampaign,
  FacebookCampaign,
  GoogleAdsSmartCampaign,
  LandingPageCampaign,
  MailrailBaseEmailCampaign,
  PinpointEmailCampaign,
  ReferralCampaign,
  SmartPinpointEmailCampaign,
  TrackedLinkCampaign,
  TTDCampaign,
} from '@feathr/blackbox';
import { CampaignClass, CampaignState } from '@feathr/blackbox';
import { Alert, Button, Spinner, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { CampaignActions } from '@feathr/extender/components';
import CampaignChips from '@feathr/extender/components/CampaignChips';
import CampaignDates from '@feathr/extender/components/CampaignDates';
import { useCampaign } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import DefaultLandingPageCampaignPage from '../CampaignRouter/LandingPageCampaignPage/DefaultLandingPageCampaignPage';
import AddCampaignButton from '../CampaignsPage/AddCampaignButton';
import GoogleAdsCampaignEdit from '../GoogleAdsCampaignPage/GoogleAdsCampaignEdit';
import AdWizard from './AdWizard';
import AutoPinpointEmailCampaignEdit from './AutoPinpointEmailCampaignEdit';
import ConversationDetails from './ConversationDetails';
import DripCampaignEdit from './DripCampaignEdit/DripCampaignEdit';
import FacebookWizard from './FacebookWizard';
import LookalikeCampaignEdit from './LookalikeCampaignEdit';
import MailrailCampaignEdit from './MailrailCampaignEdit';
import PinpointEmailCampaignEdit from './PinpointEmailCampaignEdit';
import ReferralCampaignEdit from './ReferralCampaignEdit';
import SmartPinpointEmailCampaignEdit from './SmartPinpointEmailCampaignEdit';
import TrackedLinkCampaignEdit from './TrackedLinkCampaignEdit';
const defaultWarningMessage = 'Are you sure you want to navigate away? Changes made will be lost.';

import * as styles from './CampaignEditPage.css';

export function setRedirectWarning(message = defaultWarningMessage): void {
  window.onbeforeunload = (e: BeforeUnloadEvent): string => {
    e.returnValue = message;
    return message;
  };
}

export function unsetRedirectWarning(): void {
  window.onbeforeunload = null;
}

function CampaignEditPage(): JSX.Element {
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const { campaignId } = useParams<{ eventId: string; campaignId: string }>();
  const { Campaigns } = useStore();
  const campaign: BaseCampaign = Campaigns.get(campaignId);
  const { allowed } = useCampaign({
    campaign,
  });

  if (campaign.isPending) {
    return (
      <Page>
        <Spinner className={styles.spinner} />
      </Page>
    );
  }

  function getPageTitle(pageTitle: string): string {
    // According to i18next documentation, the - needs to be added to make the key unescaped
    return t('Edit {{- name}}', { name: pageTitle });
  }

  const campaignClassName = campaign.get('_cls');
  const name = campaign.name;

  const sharedProps = {
    pageTitle: getPageTitle(name),
    title: name,
    width: 'wide' as const,
    ChipsComponent: <CampaignChips campaign={campaign} />,
    collapseHeader: false,
    collapseHeaderOnScroll: true,
    secondaryText: campaign.get('state') !== CampaignState.Draft && (
      <CampaignDates campaign={campaign} />
    ),
    actions: allowed.reportLinkInWizardHeader ? (
      <Button
        link={campaign.getItemUrl()}
        prefix={getIconForAction('back')}
        tooltip={t('Return to report')}
      >
        {t('Back')}
      </Button>
    ) : undefined,
  };

  if (campaignClassName === CampaignClass.Conversation) {
    const conversationActions = <CampaignActions campaign={campaign} />;
    return (
      <Page {...sharedProps} actions={conversationActions}>
        <ConversationDetails />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.LandingPage) {
    return <DefaultLandingPageCampaignPage campaign={campaign as LandingPageCampaign} />;
  }

  if (campaignClassName === CampaignClass.TrackedLink) {
    return (
      <Page {...sharedProps}>
        <TrackedLinkCampaignEdit campaign={campaign as TrackedLinkCampaign} />
      </Page>
    );
  }

  if ([CampaignClass.Facebook, CampaignClass.EmailListFacebook].includes(campaignClassName)) {
    return (
      <Page {...sharedProps}>
        <FacebookWizard campaign={campaign as FacebookCampaign | EmailListFacebookCampaign} />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.Referral) {
    return (
      <Page {...sharedProps}>
        <ReferralCampaignEdit campaign={campaign as ReferralCampaign} />
      </Page>
    );
  }

  if (
    [
      CampaignClass.MailrailSingleSend,
      CampaignClass.MailrailAutoSend,
      CampaignClass.MailrailDrip,
    ].includes(campaignClassName)
  ) {
    return (
      <Page {...sharedProps}>
        <MailrailCampaignEdit campaign={campaign as MailrailBaseEmailCampaign} />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.PinpointEmail) {
    return (
      <Page {...sharedProps}>
        <PinpointEmailCampaignEdit campaign={campaign as PinpointEmailCampaign} />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.SmartPinpointEmail) {
    return (
      <Page {...sharedProps}>
        <SmartPinpointEmailCampaignEdit campaign={campaign as SmartPinpointEmailCampaign} />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.AutoPinpointEmail) {
    return (
      <Page {...sharedProps}>
        <AutoPinpointEmailCampaignEdit campaign={campaign as AutoPinpointEmailCampaign} />
      </Page>
    );
  }

  if (campaignClassName === CampaignClass.Drip) {
    return (
      <Page {...sharedProps}>
        <DripCampaignEdit campaign={campaign as DripCampaign} />
      </Page>
    );
  }

  if (campaign.isTTDCampaign && campaignClassName !== CampaignClass.Lookalike) {
    return (
      <Page {...sharedProps}>
        <AdWizard campaign={campaign as TTDCampaign} />
      </Page>
    );
  }
  if (campaignClassName === CampaignClass.Lookalike) {
    return (
      <Page {...sharedProps}>
        <LookalikeCampaignEdit campaign={campaign} />
      </Page>
    );
  }
  if (
    campaignClassName === CampaignClass.GoogleAdsSmart ||
    campaignClassName === CampaignClass.GoogleAdsSearch
  ) {
    // Search and Smart campaigns share an identical field set for the flip, so
    // both render the existing Google Ads wizard typed against the Smart shape.
    return (
      <Page {...sharedProps}>
        <GoogleAdsCampaignEdit campaign={campaign as GoogleAdsSmartCampaign} />
      </Page>
    );
  }

  return (
    <Page pageTitle={getPageTitle(name)} title={name} width={'wide'}>
      <Alert
        description={
          <Trans t={t}>
            Please create a <AddCampaignButton type={'link'}>new campaign</AddCampaignButton>{' '}
            instead.
          </Trans>
        }
        title={t('This campaign type is outdated and can no longer be edited')}
        type={'warning'}
      />
      <Toolbar align={'left'}>
        <Button link={localUrl(campaign.getItemUrl())} type={'primary'}>
          {t('Back')}
        </Button>
      </Toolbar>
    </Page>
  );
}

export default observer(CampaignEditPage);
